import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BriefcaseBusiness, CalendarClock, ClipboardList, IdCard, Phone } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { ErrorState } from '@/components/common/ErrorState';
import { PersonDetailHero, type PersonDetailFact } from '@/components/common/PersonDetailHero';
import { PillBadge } from '@/components/common/PillBadge';
import { PageHeader } from '@/components/layout/PageHeader';
import { useHistoryBack } from '@/hooks/useHistoryBack';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { STICKY_SIDE_PANEL_CLASS } from '@/styles/stickySidePanel';
import { formatDisplayDate, formatTime } from '@/utils/date';
import { surveySubmissionDetailQueryOptions } from '../api/surveys.api';
import { SurveySubmissionAnswersPanel } from '../components/SurveySubmissionAnswersPanel';
import { SurveySubmissionPersonPanel } from '../components/SurveySubmissionPersonPanel';
import type { SurveySubmissionDetail } from '../types/survey.types';

function getIdentityFacts(submission: SurveySubmissionDetail | undefined): PersonDetailFact[] {
  return [
    { key: 'hrId', icon: IdCard, label: 'HR ID', value: submission?.userHrId },
    { key: 'mobile', icon: Phone, label: 'Mobile', value: submission?.mobile },
    {
      key: 'designation',
      icon: BriefcaseBusiness,
      label: 'Designation',
      value: submission?.designationName,
    },
  ];
}

interface SurveySubmissionDetailPageProps {
  surveyId: number;
  submissionId: number;
}

export function SurveySubmissionDetailPage({
  surveyId,
  submissionId,
}: SurveySubmissionDetailPageProps) {
  const navigate = useNavigate();
  const goBack = useHistoryBack(
    () => void navigate({ to: '/feedback/surveys/$surveyId/submissions', params: { surveyId } }),
  );
  useRememberViewedRow('surveySubmissions', submissionId);

  const {
    data: submission,
    error,
    refetch,
  } = useQuery(surveySubmissionDetailQueryOptions(surveyId, submissionId));

  return (
    <div className="flex flex-col gap-page-gap">
      <PageHeader title="Submission Details" leading={<BackButton onClick={goBack} />} />

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load this submission"
          onRetry={() => void refetch()}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <PersonDetailHero
            ariaLabel="Submission summary"
            name={submission?.userName}
            facts={getIdentityFacts(submission)}
            isLoading={submission === undefined}
            aside={
              submission ? (
                <div className="flex flex-wrap gap-2 sm:justify-end">
                  <PillBadge icon={ClipboardList} screenReaderLabel="Survey">
                    {submission.surveyTitle}
                  </PillBadge>
                  <PillBadge icon={CalendarClock} screenReaderLabel="Response date">
                    {`${formatDisplayDate(submission.submissionDate)}, ${formatTime(submission.submittedAt)}`}
                  </PillBadge>
                </div>
              ) : null
            }
          />

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
            <SurveySubmissionAnswersPanel answers={submission?.answers} />
            <div className={STICKY_SIDE_PANEL_CLASS}>
              <SurveySubmissionPersonPanel submission={submission} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
